import {
  EVENT_NAME_SEARCH_SUBMITTED,
  EVENT_NAME_CART_CLICKED,
  EVENT_NAME_SHORTCUT_CLICKED,
  EVENT_NAME_BRAND_BAR_CLICKED,
  EVENT_NAME_MEMBERSHIP_CLICKED,
  EVENT_NAME_SHOPPING_MENU_CLICKED,
  EVENT_NAME_GEO_MISMATCH_CLICKED,
  EVENT_NAME_GEO_SELECTION_AUTO_OPENED,
  EVENT_NAME_GEO_SELECTION_CLICKED,
  EVENT_NAME_ACCOUNT_CLICKED,
  EVENT_NAME_FOOTER_CLICKED,
  EVENT_NAME_HELP_CLICKED,
  EVENT_NAME_SEARCH_CLEAR_CLICKED,
  EVENT_NAME_SEARCH_PREVIEW_CLICKED,
  EVENT_NAME_SEARCH_TYPEAHEAD_CLICKED,
  EVENT_NAME_SEARCH_TYPEAHEAD_SHOWN,
  EVENT_NAME_SEARCH_POPULAR_SUGGESTION_CLICKED,
  EVENT_NAME_STORE_LOCATOR_CLICKED,
  EVENT_NAME_SEARCH_OPEN_CLICKED,
  EVENT_NAME_SEARCH_CLOSE_CLICKED,
  EVENT_NAME_SEARCH_RECENT_CLICKED,
  EVENT_NAME_SEARCH_RECENT_DELETED,
  EXPERIENCE_TYPE,
  SCHEMA_BASE_URL,
  SCHEMA_FILE_PATH,
  SCHEMA_MAP,
} from './constants';
import { getEventPageName, getWriteKey } from './utils';
import AnalyticsEventTypes from '../utils/analyticsEvent';
import { getLocation } from '../../utils/browser';

/**
 * Create an object that wraps the analytics client.
 * @param window
 * @param dotcomNavInstance
 * @returns {object}
 */
export default function createAnalyticsClient(window, dotcomNavInstance) {
  if (!window || !window?.webShellClient?.analytics) {
    throw new Error('Analytics client not found');
  }
  const client = window.webShellClient.analytics;
  const enums = {};
  const { country, language } = dotcomNavInstance.locale;

  /**
   * Wait for the analytics client to be initialized and for the enums to be loaded.
   * @type {Promise<Awaited<boolean>[]>}
   */
  const isInitialized = Promise.all([
    client.isInitialized(),
    client.utils.getProtoEnums('SearchType').then(SearchType => {
      enums.SearchType = SearchType;
    }),
    client.utils.getProtoEnums('UserExperience').then(UserExperience => {
      enums.UserExperience = UserExperience;
    }),
    client.utils.getProtoEnums('Search_ItemClicked_v2').then(ClickableItem => {
      enums.ClickableItem = ClickableItem;
    }),
    client.utils.getProtoEnums('Currency').then(Currency => {
      enums.Currency = Currency;
    }),
    client.utils.getProtoEnums('Nav_ItemViewed').then(NavViewableItem => {
      enums.NavViewableItem = NavViewableItem;
    }),
    client.utils.getProtoEnums('Nav_ItemClicked').then(NavItemClicked => {
      enums.NavItemClicked = NavItemClicked;
    }),
  ]);

  /**
   * Legacy track method.
   * @param eventName
   * @param event
   */
  const track = (eventName, event) => {
    const { appInfo, eventInfo } = dotcomNavInstance.opts;
    const baseEventInfo = typeof eventInfo === 'function' ? eventInfo() : eventInfo || {};
    const { viewName, viewChannel, properties = {} } = { ...baseEventInfo, ...event };

    const pageDetail = viewName ? viewName.toLowerCase() : undefined;
    const pageType = viewChannel || appInfo?.viewChannel;
    const schemaPath = SCHEMA_MAP[eventName];
    isInitialized.then(() => {
      client.track(
        eventName,
        {
          app: {
            backendPlatform: appInfo?.platform || '',
            domain: getLocation().hostname,
            version: process.env.PACKAGE_VERSION,
          },
          locale: {
            country: country.toLowerCase(),
            language: `${country}_${language}`.toLowerCase(),
          },
          view: {
            experienceType: EXPERIENCE_TYPE,
            pageName: getEventPageName(pageType, pageDetail),
            pageType: pageType ? pageType.toLowerCase() : undefined,
          },
          ...properties,
        },
        {
          $schema: `${SCHEMA_BASE_URL}/${schemaPath}/${SCHEMA_FILE_PATH}`,
          writeKey: getWriteKey(),
        },
      );
    });
  };

  const trackAction = clickStreamData => {
    isInitialized.then(() => {
      // Enums may not be ready at this point so we need to wait for isInitialized to resolve first.
      const data = typeof clickStreamData === 'function' ? clickStreamData() : clickStreamData;
      /**
       * For all v2 actions. Must be changed when v3 is released.
       * https://github.com/nike-data-quality-engine/clickstream-protobuf-definitions/blob/main/proto/nike/clickstream/event/web/dotcom/v2/action.proto
       */
      client.trackAction(data, { eventVersion: 2 });
    });
  };

  const typeAheadSuggestionShown = ({ searchTerm }) => {
    const searchType = 'user typed';
    track(EVENT_NAME_SEARCH_TYPEAHEAD_SHOWN, {
      properties: {
        searchTerm,
        searchTermType: `${searchType}:${searchTerm}`,
        searchType,
      },
    });
  };
  const clickCloseMobileMenu = () => {
    const mouseTarget = 'nav:menu close';
    track(EVENT_NAME_SHORTCUT_CLICKED, {
      properties: {
        clickActivity: mouseTarget,
      },
    });
  };
  const clickGeoMismatchConfirm = ({ country, language }) => {
    track(EVENT_NAME_GEO_MISMATCH_CLICKED, {
      properties: {
        clickActivity: `nav:geo mismatch:confirm:${country || ''}:${language || ''}`,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceNavV2ItemClicked',
        value: {
          item: enums.NavItemClicked.GEOMISMATCH_LEAVE,
        },
      },
    }));
  };
  const clickGeoMismatchDismiss = () => {
    track(EVENT_NAME_GEO_MISMATCH_CLICKED, {
      properties: {
        clickActivity: 'nav:geo mismatch:close',
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceNavV2ItemClicked',
        value: {
          item: enums.NavItemClicked.GEOMISMATCH_CLOSE,
        },
      },
    }));
  };
  const clickGeoMismatchStay = () => {
    track(EVENT_NAME_GEO_MISMATCH_CLICKED, {
      properties: {
        clickActivity: 'nav:geo mismatch:close',
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceNavV2ItemClicked',
        value: {
          item: enums.NavItemClicked.GEOMISMATCH_STAY,
        },
      },
    }));
  };
  const clickGeoMismatchShown = ({ country }) => {
    track(EVENT_NAME_GEO_MISMATCH_CLICKED, {
      properties: {
        clickActivity: `nav:geo mismatch:displayed:${country}`,
      },
    });
    isInitialized.then(() => {
      trackAction(() => ({
        underlyingAction: {
          case: 'uxCommerceNavV2ItemViewed',
          value: {
            item: enums.NavViewableItem.GEOMISMATCH,
          },
        },
      }));
    });
  };
  const clickHeaderLogo = ({ dataset }) => {
    track(EVENT_NAME_BRAND_BAR_CLICKED, {
      properties: {
        clickActivity: `nav:${dataset?.brand}:home`,
      },
    });
  };
  const clickNavAccount = ({ path, hashKey }) => {
    track(EVENT_NAME_ACCOUNT_CLICKED, {
      properties: {
        clickActivity: `nav:${(path || '').split(':').slice(0, 2).join(':')}`,
      },
    });
    if (hashKey) {
      trackAction({
        underlyingAction: {
          case: 'uxCommerceNavV2LinkClicked',
          value: {
            item: {
              analyticsKey: hashKey,
            },
          },
        },
      });
    }
  };
  const clickNavBrandBar = ({ label, hashKey }) => {
    track(EVENT_NAME_BRAND_BAR_CLICKED, {
      properties: {
        clickActivity: `nav:${label || ''}`,
      },
    });
    if (hashKey) {
      trackAction({
        underlyingAction: {
          case: 'uxCommerceNavV2LinkClicked',
          value: {
            item: {
              analyticsKey: hashKey,
            },
          },
        },
      });
    }
  };
  const clickNavCart = () => {
    track(EVENT_NAME_CART_CLICKED, {
      viewChannel: 'cart', // populates properties.view.pageType
      properties: {
        clickActivity: 'nav:cart',
      },
    });
  };
  const clickNavCountrySelectionAutoOpen = ({ supported, country }) => {
    const mouseTarget = supported
      ? `nav:geo selection:open:language tunnel:${country || ''}`
      : `nav:geo selection:open:country not supported:${country || ''}`;
    track(EVENT_NAME_GEO_SELECTION_AUTO_OPENED, {
      properties: {
        clickActivity: mouseTarget,
      },
    });
  };
  const clickNavCountrySelectionDismiss = ({ country }) => {
    track(EVENT_NAME_GEO_SELECTION_CLICKED, {
      properties: {
        clickActivity: `nav:geo selection:close:${country || ''}`,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceNavV2ItemClicked',
        value: {
          item: enums.NavItemClicked.LANGUAGE_TUNNEL_CLOSE,
        },
      },
    }));
  };
  const clickNavCountrySelectionOpen = ({ country }) => {
    track(EVENT_NAME_GEO_SELECTION_CLICKED, {
      properties: {
        clickActivity: `nav:geo selection:open:${country || ''}`,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceNavV2ItemViewed',
        value: {
          item: enums.NavViewableItem.LANGUAGE_TUNNEL,
        },
      },
    }));
  };
  const clickNavCountrySelectionSuccess = ({ country, language }) => {
    track(EVENT_NAME_GEO_SELECTION_CLICKED, {
      properties: {
        clickActivity: `nav:geo selection:selected:${country || ''}:${language || ''}`,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceNavV2ItemClicked',
        value: {
          item: enums.NavItemClicked.LANGUAGE_TUNNEL_ITEM,
        },
      },
    }));
  };
  const clickNavFooter = ({ path, hashKey }) => {
    track(EVENT_NAME_FOOTER_CLICKED, {
      properties: {
        clickActivity: `nav:footer:${path || ''}`,
      },
    });
    if (hashKey) {
      trackAction({
        underlyingAction: {
          case: 'uxCommerceNavV2LinkClicked',
          value: {
            item: {
              analyticsKey: hashKey,
            },
          },
        },
      });
    }
  };
  const clickNavHelpMenu = ({ path, hashKey }) => {
    track(EVENT_NAME_HELP_CLICKED, {
      properties: {
        clickActivity: `nav:${path || ''}`,
      },
    });
    if (hashKey) {
      trackAction({
        underlyingAction: {
          case: 'uxCommerceNavV2LinkClicked',
          value: {
            item: {
              analyticsKey: hashKey,
            },
          },
        },
      });
    }
  };
  const clickNavMembership = ({ path }) => {
    track(EVENT_NAME_MEMBERSHIP_CLICKED, {
      properties: {
        clickActivity: `nav:${path || ''}`,
      },
    });
  };
  const clickNavMobileHelpMenu = ({ path }) => {
    track(EVENT_NAME_HELP_CLICKED, {
      properties: {
        clickActivity: `nav:menu:${path || ''}`,
      },
    });
  };
  const clickSearchTypeAheadSuggestions = ({ searchTerm }) => {
    const searchType = 'user typed';
    track(EVENT_NAME_SEARCH_TYPEAHEAD_CLICKED, {
      properties: {
        searchTerm,
        searchTermType: `${searchType}:${searchTerm}`,
        searchType,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'coreCommerceV1SearchSubmitted',
        value: {
          search: {
            searchType: enums.SearchType.TYPEAHEAD,
            searchTerms: searchTerm,
            searchText: searchTerm,
          },
        },
      },
    }));
  };
  const clickNavShoppingMenu = ({ path, hashKey }) => {
    const mouseTarget = `nav:shop>${(path || '')
      .replace(/:/g, '>')
      .replace('header>', '')
      .replace(/^root>/, '')}`;
    track(EVENT_NAME_SHOPPING_MENU_CLICKED, {
      properties: {
        clickActivity: mouseTarget,
      },
    });
    if (hashKey) {
      trackAction({
        underlyingAction: {
          case: 'uxCommerceNavV2LinkClicked',
          value: {
            item: {
              analyticsKey: hashKey,
            },
          },
        },
      });
    }
  };
  const clickNavShortcut = ({ path }) => {
    track(EVENT_NAME_SHORTCUT_CLICKED, {
      viewChannel: path === 'menu:favorites' ? 'favorites' : undefined,
      properties: {
        clickActivity: `nav:${path || ''}`,
      },
    });
  };
  const clickNavShortcutMobile = ({ path }) => {
    track(EVENT_NAME_SHORTCUT_CLICKED, {
      properties: {
        clickActivity: `nav:menu:${path || ''}`,
      },
    });
  };
  const clickNavStoreLocator = () => {
    track(EVENT_NAME_STORE_LOCATOR_CLICKED, {
      properties: {
        clickActivity: 'nav:find a store',
      },
    });
  };
  const clickOpenMobileMenu = () => {
    track(EVENT_NAME_SHORTCUT_CLICKED, {
      properties: {
        clickActivity: 'nav:menu open',
      },
    });
  };
  const clickPopularSearchSelection = ({ path }) => {
    const searchTerm = path || '';
    const searchType = 'popular term';
    track(EVENT_NAME_SEARCH_POPULAR_SUGGESTION_CLICKED, {
      properties: {
        searchTerm,
        searchTermType: `${searchType}:${searchTerm}`,
        searchType,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'coreCommerceV1SearchSubmitted',
        value: {
          search: {
            searchType: enums.SearchType.POPULAR_TERM,
            searchTerms: searchTerm,
          },
        },
      },
    }));
  };
  const clickSearchClear = () => {
    track(EVENT_NAME_SEARCH_CLEAR_CLICKED, {
      properties: {
        clickActivity: 'nav:search:cleared',
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceSearchV2ItemClicked',
        value: {
          item: enums.ClickableItem.SEARCH_CLEAR,
        },
      },
    }));
  };
  const clickSearchOpen = () => {
    track(EVENT_NAME_SEARCH_OPEN_CLICKED, {
      properties: {
        clickActivity: 'nav:search:open',
      },
    });
  };
  const clickSearchClose = () => {
    track(EVENT_NAME_SEARCH_CLOSE_CLICKED, {
      properties: {
        clickActivity: 'nav:search:close',
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceSearchV2ItemClicked',
        value: {
          item: enums.ClickableItem.SEARCH_CANCEL,
        },
      },
    }));
  };
  const clickSearchRecentDelete = ({ search }) => {
    track(EVENT_NAME_SEARCH_RECENT_DELETED, {
      properties: {
        clickActivity: 'nav:search:recent:removed',
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceSearchV2ItemClicked',
        value: {
          item: enums.ClickableItem.SEARCH_HISTORY_CLEAR,
          searchTerms: search,
          searchText: search,
        },
      },
    }));
  };
  const clickSearchRecentSubmit = ({ path }) => {
    const searchTerm = path || '';
    const searchType = 'recent';
    track(EVENT_NAME_SEARCH_RECENT_CLICKED, {
      properties: {
        clickActivity: 'nav:search:recent:submit',
        searchTerm: searchTerm,
        searchTermType: `${searchType}:${searchTerm}`,
        searchType,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'coreCommerceV1SearchSubmitted',
        value: {
          search: {
            searchType: enums.SearchType.RECENT_TERM,
            searchTerms: searchTerm,
          },
        },
      },
    }));
  };
  const submitNavOnsiteSearchUserTyped = ({ searchItem }) => {
    const searchType = 'user typed';
    track(EVENT_NAME_SEARCH_SUBMITTED, {
      properties: {
        searchTerm: searchItem,
        searchTermType: `${searchType}:${searchItem}`,
        searchType,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'coreCommerceV1SearchSubmitted',
        value: {
          search: {
            searchType: enums.SearchType.USER_TYPED,
            searchTerms: searchItem,
            searchText: searchItem,
          },
        },
      },
    }));
  };
  const visualSearchItemClick = ({ searchTerm, itemSelected }) => {
    const searchType = 'search preview';
    track(EVENT_NAME_SEARCH_PREVIEW_CLICKED, {
      properties: {
        products: [
          {
            productId: itemSelected,
          },
        ],
        searchTerm,
        searchTermType: `${searchType}:${searchTerm}`,
        searchType,
      },
    });
    trackAction(() => ({
      underlyingAction: {
        case: 'coreCommerceV1SearchSubmitted',
        value: {
          search: {
            searchType: enums.SearchType.SEARCH_PREVIEW,
            searchTerms: searchTerm,
            searchText: searchTerm,
          },
        },
      },
    }));
  };

  const visualSearchCollectionViewed = ({ searchTerm, collection }) => {
    trackAction({
      underlyingAction: {
        case: 'uxCommerceSearchV2CollectionViewed',
        value: {
          collection: {
            underlyingCollection: {
              case: `searchPreview`,
              value: {
                searchTerms: searchTerm,
                searchText: searchTerm,
              },
            },
            itemCount: collection.length,
          },
        },
      },
    });
  };
  const actionSearchUserExperienceViewed = async () => {
    trackAction(() => ({
      underlyingAction: {
        case: 'userExperienceViewed',
        value: enums.UserExperience.COMMERCE_SEARCH,
      },
    }));
  };

  const searchCollectionItemViewed = ({ index, product, visualSearchResults, searchText }) => {
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceSearchV2CollectionItemViewed',
        value: {
          collectionItem: {
            collection: {
              underlyingCollection: {
                case: `searchPreview`,
                value: {
                  searchTerms: searchText,
                  searchText: searchText,
                },
              },
              itemCount: visualSearchResults.length,
            },
            index: index,
            underlyingItem: {
              case: 'product',
              value: {
                productId: {
                  case: 'merchProductId',
                  value: product.globalProductId,
                },
                currency: enums.Currency[product.prices.currency],
                fullPrice: client.utils.toUnitsNanos(product.prices.initialPrice),
                currentPrice: client.utils.toUnitsNanos(product.prices.currentPrice),
              },
            },
          },
        },
      },
    }));
  };

  const searchCollectionItemClicked = ({ searchText, product, length, index }) => {
    trackAction(() => ({
      underlyingAction: {
        case: 'uxCommerceSearchV2CollectionItemClicked',
        value: {
          collectionItem: {
            collection: {
              underlyingCollection: {
                case: `searchPreview`,
                value: {
                  searchTerms: searchText,
                  searchText: searchText,
                },
              },
              itemCount: length,
            },
            index: index,
            underlyingItem: {
              case: 'product',
              value: {
                productId: {
                  case: 'merchProductId',
                  value: product.globalProductId,
                },
                currency: enums.Currency[product.prices.currency],
                fullPrice: client.utils.toUnitsNanos(product.prices.initialPrice),
                currentPrice: client.utils.toUnitsNanos(product.prices.currentPrice),
              },
            },
          },
        },
      },
    }));
  };

  const recentlyViewedExperienceViewed = () => {
    trackAction(() => ({
      underlyingAction: {
        case: 'temporaryAction',
        value: {
          actionName: 'recentlyViewedExperienceViewed',
          type: 1, // view
        },
      },
    }));
  };

  const recentlyViewedProductClicked = ({ productId }) => {
    trackAction(() => ({
      underlyingAction: {
        case: 'temporaryAction',
        value: {
          actionName: 'recentlyViewedProductClicked',
          type: 3, // custom
          data: {
            productId,
          },
        },
      },
    }));
  };

  const recentlyViewedProductsCleared = () => {
    trackAction(() => ({
      underlyingAction: {
        case: 'temporaryAction',
        value: {
          actionName: 'recentlyViewedProductsCleared',
          type: 2, // click
        },
      },
    }));
  };

  const lookup = analyticsPayload => {
    const { type, props } = analyticsPayload;
    switch (type) {
      case AnalyticsEventTypes.CLICK_NAV_ACCOUNT:
        return clickNavAccount({ path: props.path, hashKey: props.hashKey });
      case AnalyticsEventTypes.CLICK_NAV_CART:
        return clickNavCart();
      case AnalyticsEventTypes.CLICK_HELP_MENU:
        return clickNavHelpMenu({ path: props.path, hashKey: props.hashKey });
      case AnalyticsEventTypes.CLICK_NAV_MEMBERSHIP:
        return clickNavMembership({ path: props.path });
      case AnalyticsEventTypes.CLICK_NAV_STORE_LOCATOR:
        return clickNavStoreLocator();
      case AnalyticsEventTypes.SHOPPING_EVENT:
        return clickNavShoppingMenu({ path: props.path, hashKey: props.hashKey });
      case AnalyticsEventTypes.CLICK_NAV_SHORTCUT:
        return clickNavShortcut({ path: props.path });
      case AnalyticsEventTypes.CLICK_NAV_FOOTER:
        return clickNavFooter({ path: props.path, hashKey: props.hashKey });
      default:
        throw new Error(`Unhandled analyticsEventName: ${type}`);
    }
  };
  return {
    typeAheadSuggestionShown,
    clickGeoMismatchConfirm,
    clickGeoMismatchDismiss,
    clickGeoMismatchStay,
    clickGeoMismatchShown,
    clickHeaderLogo,
    clickNavAccount,
    clickNavBrandBar,
    clickNavCart,
    clickNavCountrySelectionAutoOpen,
    clickNavCountrySelectionDismiss,
    clickNavCountrySelectionOpen,
    clickNavCountrySelectionSuccess,
    clickNavFooter,
    clickNavHelpMenu,
    clickNavMembership,
    clickNavMobileHelpMenu,
    clickNavShortcut,
    clickNavShortcutMobile,
    clickNavStoreLocator,
    clickNavShoppingMenu,
    clickOpenMobileMenu,
    clickPopularSearchSelection,
    clickSearchTypeAheadSuggestions,
    clickSearchClear,
    clickSearchOpen,
    clickSearchClose,
    clickSearchRecentDelete,
    clickSearchRecentSubmit,
    submitNavOnsiteSearchUserTyped,
    visualSearchItemClick,
    visualSearchCollectionViewed,
    searchCollectionItemViewed,
    clickCloseMobileMenu,
    actionSearchUserExperienceViewed,
    searchCollectionItemClicked,
    recentlyViewedExperienceViewed,
    recentlyViewedProductClicked,
    recentlyViewedProductsCleared,
    lookup,
    track,
    trackAction,
  };
}
